import { defineStore } from 'pinia';
import { addDoc, collection, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { toMillis } from '~/lib/timestamps';
import type { Comment } from '~/types/models';
import { CORE_DATA_MAX_AGE_MS, chunkRequestValues, runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';

type CommentEntityType = Comment['entityType'];

export const useCommentsStore = defineStore('comments', {
    state: () => ({
        commentsByEntity: {} as Record<string, Comment[]>,
        loadingByEntity: {} as Record<string, boolean>,
        loadedAtByEntity: {} as Record<string, number>,
        commentsError: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            const portalStore = useClientPortalStore();
            const tenantId = tenantStore.currentTenantId || portalStore.currentTenantId;
            if (!tenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantId;
        },

        _entityKey(entityType: CommentEntityType, entityId: string): string {
            return `${entityType}:${entityId}`;
        },

        _sortComments(comments: Comment[]): Comment[] {
            return [...comments].sort((a, b) => {
                const aDate = toMillis(a.createdAt);
                const bDate = toMillis(b.createdAt);
                return aDate - bDate;
            });
        },

        async fetchComments(entityType: CommentEntityType, entityId: string, options: FreshRequestOptions = {}): Promise<Comment[]> {
            const { $db } = useNuxtApp();
            this.commentsError = null;

            const tenantId = this._getTenantId();
            const entityKey = this._entityKey(entityType, entityId);
            if (!options.force
                && Object.prototype.hasOwnProperty.call(this.commentsByEntity, entityKey)
                && Date.now() - (this.loadedAtByEntity[entityKey] || 0) < (options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS)) {
                return this.commentsByEntity[entityKey] || [];
            }
            this.loadingByEntity[entityKey] = true;

            try {
                const collectionRef = collection($db, `tenants/${tenantId}/comments`);
                const q = query(
                    collectionRef,
                    where('entityType', '==', entityType),
                    where('entityId', '==', entityId),
                );
                const snap = await getDocs(q);

                const comments = snap.docs.map((commentDoc) => ({
                    id: commentDoc.id,
                    ...commentDoc.data(),
                } as Comment));

                this.commentsByEntity[entityKey] = this._sortComments(comments);
                this.loadedAtByEntity[entityKey] = Date.now();
                return this.commentsByEntity[entityKey] || [];
            } catch (error: any) {
                this.commentsError = error.message || 'Failed to fetch comments';
                console.error('Error fetching comments:', error);
                return [];
            } finally {
                this.loadingByEntity[entityKey] = false;
            }
        },

        async fetchCommentsForEntities(
            entityType: CommentEntityType,
            entityIds: string[],
            options: FreshRequestOptions = {},
        ): Promise<Comment[]> {
            const normalizedEntityIds = Array.from(new Set(entityIds.map(id => String(id || '').trim()).filter(Boolean))).sort();
            if (normalizedEntityIds.length === 0) return [];

            const tenantId = this._getTenantId();
            const entityKeys = normalizedEntityIds.map(entityId => this._entityKey(entityType, entityId));
            const hasData = entityKeys.every(entityKey => Object.prototype.hasOwnProperty.call(this.commentsByEntity, entityKey));
            const maxAgeMs = options.maxAgeMs ?? CORE_DATA_MAX_AGE_MS;
            if (!options.force && hasData && entityKeys.every(entityKey => (
                Date.now() - (this.loadedAtByEntity[entityKey] || 0) < maxAgeMs
            ))) {
                return entityKeys.flatMap(entityKey => this.commentsByEntity[entityKey] || []);
            }

            await runFreshRequest(`comments:${tenantId}:${entityType}:${normalizedEntityIds.join(',')}`, async () => {
                const { $db } = useNuxtApp();
                entityKeys.forEach((entityKey) => {
                    this.loadingByEntity[entityKey] = true;
                    this.commentsByEntity[entityKey] = [];
                });

                try {
                    const snapshots = await Promise.all(chunkRequestValues(normalizedEntityIds).map((entityIdChunk) => (
                        getDocs(query(
                            collection($db, `tenants/${tenantId}/comments`),
                            where('entityType', '==', entityType),
                            where('entityId', 'in', entityIdChunk),
                        ))
                    )));
                    for (const snapshot of snapshots) {
                        for (const commentDoc of snapshot.docs) {
                            const comment = { id: commentDoc.id, ...commentDoc.data() } as Comment;
                            const entityKey = this._entityKey(comment.entityType, comment.entityId);
                            this.commentsByEntity[entityKey] = this._sortComments([
                                ...(this.commentsByEntity[entityKey] || []),
                                comment,
                            ]);
                        }
                    }
                    entityKeys.forEach((entityKey) => {
                        this.loadedAtByEntity[entityKey] = Date.now();
                    });
                } finally {
                    entityKeys.forEach((entityKey) => {
                        this.loadingByEntity[entityKey] = false;
                    });
                }
            }, { ...options, maxAgeMs, hasData });

            return entityKeys.flatMap(entityKey => this.commentsByEntity[entityKey] || []);
        },

        async addComment(entityType: CommentEntityType, entityId: string, content: string): Promise<string> {
            const { $db } = useNuxtApp();
            this.commentsError = null;

            const trimmedContent = content.trim();
            if (!trimmedContent) {
                throw new Error('Comment content is required.');
            }

            const tenantId = this._getTenantId();
            const entityKey = this._entityKey(entityType, entityId);

            const authStore = useAuthStore();
            const userStore = useUserStore();
            const userId = authStore.user?.uid || userStore.user?.id;
            if (!userId) {
                throw new Error('You must be logged in to comment.');
            }

            const authorName = authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Unknown';
            const authorAvatar = authStore.user?.photoURL || userStore.user?.avatarURL || '';

            const commentData = {
                tenantId,
                entityType,
                entityId,
                entityKey,
                content: trimmedContent,
                authorId: userId,
                authorName,
                authorAvatar,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            try {
                const collectionRef = collection($db, `tenants/${tenantId}/comments`);
                const docRef = await addDoc(collectionRef, commentData);

                const createdComment = {
                    id: docRef.id,
                    ...commentData,
                } as Comment;

                const existing = this.commentsByEntity[entityKey] || [];
                this.commentsByEntity[entityKey] = this._sortComments([...existing, createdComment]);

                const activityStore = useActivityStore();
                try {
                    await activityStore.logActivity({
                        action: 'comment',
                        entityType,
                        entityId,
                        description: `Added a comment`,
                        userId,
                        userName: authorName,
                        userAvatar: authorAvatar,
                    });
                } catch (activityError) {
                    console.warn('Failed to log comment activity:', activityError);
                }

                if (entityType === 'task') {
                    try {
                        const taskStore = useTaskStore();
                        const { tenantPath } = useTenantPath();
                        const task = taskStore.tasks.find((entry) => entry.id === entityId) || await taskStore.fetchTaskById(entityId);
                        const participantIds = Array.from(new Set(
                            [
                                ...(Array.isArray(task?.assigneeIds) ? task.assigneeIds : []),
                                task?.assigneeId,
                                task?.owner,
                                ...existing.map((comment) => comment.authorId),
                            ]
                                .map((value) => String(value || '').trim())
                                .filter(Boolean),
                        ));

                        if (task && participantIds.length > 0) {
                            await dispatchNotification({
                                tenantId,
                                surface: 'tenant',
                                eventType: 'task_comment_reply',
                                title: `New comment on ${task.title || 'task'}`,
                                body: `${authorName} added a comment.`,
                                recipients: [{ type: 'tenant_user_ids', userIds: participantIds }],
                                entityType,
                                entityId,
                                projectId: task.projectId,
                                clientId: task.clientId,
                                actionPath: tenantPath({ path: '/tasks', query: { taskId: entityId } }),
                                actionLabel: 'Open task',
                                actorId: userId,
                                actorName: authorName,
                                actorAvatarURL: authorAvatar,
                            });
                        }
                    } catch (notificationError) {
                        console.warn('Failed to dispatch task comment notification:', notificationError);
                    }
                }

                return docRef.id;
            } catch (error: any) {
                this.commentsError = error.message || 'Failed to add comment';
                console.error('Error adding comment:', error);
                throw error;
            }
        },

        async deleteComment(entityType: CommentEntityType, entityId: string, commentId: string): Promise<void> {
            const { $db } = useNuxtApp();
            this.commentsError = null;

            try {
                const tenantId = this._getTenantId();
                const entityKey = this._entityKey(entityType, entityId);
                const commentRef = doc($db, `tenants/${tenantId}/comments`, commentId);
                await deleteDoc(commentRef);

                const existing = this.commentsByEntity[entityKey] || [];
                this.commentsByEntity[entityKey] = existing.filter((comment) => comment.id !== commentId);
            } catch (error: any) {
                this.commentsError = error.message || 'Failed to delete comment';
                console.error('Error deleting comment:', error);
                throw error;
            }
        },
    },
});
